import { db } from '../../firestoreConfig';
import { v4 as uuidv4 } from 'uuid';
import { DOCUMENT_LISTENER } from '../../shared/types/firestoreType'
import {
    collection,
    doc,
    getDoc,
    getDocs,
    query,
    writeBatch,
    serverTimestamp,
    onSnapshot
} from "firebase/firestore";
import global from '../../shared/utility/global';

export class CommonCloudFirestore {
    constructor(path, collections) {
        this.path = path;
        this.collections = collections ?? [];
    }

    async getDocuments(condition01, condition02, condition03, condition04) {
        var _query;

        if (condition01 && condition02 && condition03 && condition04)
            _query = query(collection(db, this.path), condition01, condition02, condition03, condition04);
        else if (condition01 && condition02 && condition03)
            _query = query(collection(db, this.path), condition01, condition02, condition03);
        else if (condition01 && condition02)
            _query = query(collection(db, this.path), condition01, condition02);
        else if (condition01)
            _query = query(collection(db, this.path), condition01);
        else
            _query = query(collection(db, this.path));

        const querySnapshot = await getDocs(_query);
        return global.formatDocuments(querySnapshot);
    };

    async addDocument(documentId, documentData, listeners) {
        documentData.updateDate = serverTimestamp();
        const documentRef = doc(db, this.path, documentId);
        const batch = writeBatch(db);

        batch.set(documentRef, documentData);

        listeners?.forEach(item => {
            const listenerRef = doc(db, DOCUMENT_LISTENER, item);
            batch.set(listenerRef, { value: uuidv4() });
        });

        var result = await batch.commit().then(() => true).catch((error) => false);
        return result;
    };

    async upateDocument(documentId, documentData, listeners) {
        documentData.updateDate = serverTimestamp();
        const batch = writeBatch(db);
        const documentRef = doc(db, this.path, documentId);

        batch.update(documentRef, documentData);

        listeners?.forEach(item => {
            const listenerRef = doc(db, DOCUMENT_LISTENER, item);
            batch.set(listenerRef, { value: uuidv4() });
        });

        var result = await batch.commit().then(() => true).catch((error) => false);
        return result;
    };

    async setDocument(documentId, documentData, listeners) {
        documentData.updateDate = serverTimestamp();

        const batch = writeBatch(db);
        const documentRef = ['', null, undefined].includes(documentId)
            ? doc(collection(db, this.path))
            : doc(db, this.path, documentId);
        const documents = documentData.documents?.map(m => ({ ...m }));

        delete documentData['documents'];

        if (['', null, undefined].includes(documentId))
            batch.set(documentRef, documentData);
        else
            batch.update(documentRef, documentData);

        listeners?.forEach(item => {
            const listenerRef = doc(db, DOCUMENT_LISTENER, item);
            batch.set(listenerRef, { value: uuidv4() });
        });

        this.collections?.forEach(item => {
            documents?.filter(m => m.collection === item)
                .forEach(subItem => {
                    const _documentRef = doc(documentRef, subItem.collection, subItem.documentId);
                    batch.set(_documentRef, subItem.documentData);
                });
        });

        var result = await batch.commit().then(() => true).catch((error) => false);
        return result;
    };

    async setDocuments(documents, listeners) {
        const batch = writeBatch(db);
        documents.forEach(documentData => {
            documentData.updateDate = serverTimestamp();
            const documentRef = doc(db, this.path, documentData.id);
            delete documentData['id'];
            batch.set(documentRef, documentData);

            listeners?.forEach(item => {
                const listenerRef = doc(db, DOCUMENT_LISTENER, item);
                batch.set(listenerRef, { value: uuidv4() });
            });
        });

        var result = await batch.commit().then(() => true).catch((error) => false);
        return result;
    };

    async deleteDocument(documentId, listeners) {
        const batch = writeBatch(db);
        const documentRef = doc(db, this.path, documentId);

        batch.delete(documentRef);

        var _collections = [];
        for (let index = 0; index < this.collections.length; index++) {
            var _documents = await getDocs(collection(documentRef, this.collections[index]));
            _collections.push({ collection: this.collections[index], documents: global.formatDocuments(_documents) });
        }

        _collections.forEach(item => {
            item.documents?.forEach(subItem => {
                const _documentRef = doc(documentRef, item.collection, subItem.id);
                batch.delete(_documentRef);
            });
        });

        listeners?.forEach(item => {
            const listenerRef = doc(db, DOCUMENT_LISTENER, item);
            batch.set(listenerRef, { value: uuidv4() });
        });

        var result = await batch.commit().then(() => true).catch(() => false);
        return result;
    };

    async getDocumentById(documentId) {
        try {
            var documentRef = doc(db, this.path, documentId);
            var _document = await getDoc(documentRef);

            if (!_document.exists())
                return undefined;

            var _collections = [];
            for (let index = 0; index < this.collections.length; index++) {
                var _documents = await getDocs(collection(documentRef, this.collections[index]));
                _collections.push({ collection: this.collections[index], documents: global.formatDocuments(_documents) });
            }

            var result = {};
            result.document = { id: _document.id, ..._document.data() };

            if (this.collections.length === 0)
                return result.document;

            for (let index = 0; index < _collections.length; index++) {
                result[_collections[index].collection + 'Collection'] = _collections[index].documents;
            }

            return result;
        } catch (error) {
            return undefined;
        }
    };

    async updatePositions(documents, listeners) {
        const batch = writeBatch(db);
        documents.forEach(item => {
            var documentRef = doc(db, this.path, item.id);
            batch.update(documentRef, { index: item.index, updateDate: serverTimestamp() });
        });

        listeners?.forEach(item => {
            const listenerRef = doc(db, DOCUMENT_LISTENER, item);
            batch.set(listenerRef, { value: uuidv4() });
        });

        var result = await batch.commit().then(() => true).catch(() => false);
        return result;
    };

    async onSnapshotDocuments(action, condition01, condition02, condition03, condition04) {
        var _query;

        if (condition01 && condition02 && condition03 && condition04)
            _query = query(collection(db, this.path), condition01, condition02, condition03, condition04);
        else if (condition01 && condition02 && condition03)
            _query = query(collection(db, this.path), condition01, condition02, condition03);
        else if (condition01 && condition02)
            _query = query(collection(db, this.path), condition01, condition02);
        else if (condition01)
            _query = query(collection(db, this.path), condition01);
        else
            _query = query(collection(db, this.path));

        onSnapshot(_query, (querySnapshot) => {
            const documents = global.formatDocuments(querySnapshot);
            action(documents);
        });
    };

    async onSnapshotDocumentById(action, documentId) {
        var documentRef = doc(db, this.path, documentId);
        onSnapshot(documentRef, (doc) => {
            var document = { id: doc.id, ...doc.data() }
            action(document);
        });
    };
}